import React, { forwardRef, useCallback, useState } from 'react';
import { StyleSheet, Text, TextInput, View } from 'react-native';
import { getComposerContentHeight } from './textareaLayout';

// A controlled value update does not always emit onContentSizeChange on native.
// Measure a separate copy, including wrapping and Dynamic Type, but keep it out
// of layout flow. The live input must not participate in intrinsic sizing: a
// resize while UIKit/Android scrolls to the caret can undo that scroll.
const NativeComposerTextarea = forwardRef(function NativeComposerTextarea({
  value = '', style, maxFontSizeMultiplier, allowFontScaling, scrollEnabled = true, ...props
}, ref) {
  const { height, minHeight, maxHeight, ...textStyle } = StyleSheet.flatten(style) || {};
  const minimumHeight = minHeight || 46;
  const maximumHeight = maxHeight || 240;
  const [measuredHeight, setMeasuredHeight] = useState(minimumHeight);
  const viewportHeight = getComposerContentHeight(measuredHeight, minimumHeight, maximumHeight);
  const handleSizingLayout = useCallback(event => {
    const next = event?.nativeEvent?.layout?.height;
    if (typeof next !== 'number' || !Number.isFinite(next) || next < 0) return;
    // Retain the measurement for a later, larger keyboard/rotation cap even if
    // the sizing copy's frame does not change. Only the clamped viewport sizes
    // the input; an overflowing document cannot move its frame on each edit.
    const rounded = Math.ceil(next);
    setMeasuredHeight(previous => previous === rounded ? previous : rounded);
  }, []);
  // The sizing copy only needs enough lines to fill the visible composer.
  // Without a line limit it lays out an arbitrarily tall, invisible document
  // again on every keystroke. Keep the actual input's complete draft intact.
  // One extra line covers fractional line heights and the zero-width sentinel.
  const sizingLineLimit = textStyle.lineHeight > 0
    ? Math.ceil(maximumHeight / textStyle.lineHeight) + 1 : undefined;
  return (
    <View style={{ height: viewportHeight, minHeight, maxHeight, overflow: 'hidden' }}>
      <Text
        accessible={false}
        accessibilityElementsHidden
        importantForAccessibility="no-hide-descendants"
        pointerEvents="none"
        maxFontSizeMultiplier={maxFontSizeMultiplier}
        allowFontScaling={allowFontScaling}
        numberOfLines={sizingLineLimit}
        ellipsizeMode="clip"
        onLayout={handleSizingLayout}
        style={[textStyle, { opacity: 0, position: 'absolute', top: 0, left: 0, right: 0 }]}
      >
        {/* Even an empty input needs a line of scaled text plus its padding. */}
        {`${value}\u200b`}
      </Text>
      <TextInput
        {...props}
        ref={ref}
        value={value}
        maxFontSizeMultiplier={maxFontSizeMultiplier}
        allowFontScaling={allowFontScaling}
        scrollEnabled={scrollEnabled && viewportHeight >= maximumHeight}
        style={[textStyle, StyleSheet.absoluteFillObject, { textAlignVertical: 'top' }]}
      />
    </View>
  );
});

export default NativeComposerTextarea;
